import { matchPath } from 'react-router-dom';

export interface Crumb {
  label: string;
  to?: string;
}

interface ProjectLookupEntry {
  id: string;
  name: string;
}

// Source of truth for visible breadcrumb labels. Every routed page
// SHOULD have a pattern here so the breadcrumb never falls through to
// a raw URL segment. Project-scoped routes (`/projects/:id/...`) live
// alongside their flat siblings (`/csdb`, `/ai`, etc.) so an old
// bookmark or a project-scoped deep link both label correctly.
export const ROUTE_LABELS: { pattern: string; label: string }[] = [
  { pattern: '/csdb', label: 'CSDB' },
  { pattern: '/import', label: 'Import' },
  { pattern: '/graphics', label: 'Graphics & ICN' },
  { pattern: '/applicability', label: 'Applicability & CIR' },
  { pattern: '/publishing', label: 'Publishing' },
  { pattern: '/review', label: 'Review & Changes' },
  { pattern: '/manuals', label: 'HTML Manuals' },
  { pattern: '/ai', label: 'AI' },
  // `/projects` is the renamed Home landing.
  { pattern: '/projects', label: 'Home' },
  // Per-project routes — needed so deep links don't expose raw URL
  // segments in the breadcrumb. The `:id` crumb is handled separately
  // (it's replaced by the project name + linked to `/projects/:id/
  // dashboard` — see `buildCrumbs`).
  { pattern: '/projects/:id/dashboard', label: 'Dashboard' },
  { pattern: '/projects/:id/settings', label: 'Project Settings' },
  { pattern: '/projects/:id/users', label: 'Project Users' },
  { pattern: '/projects/:id/manuals', label: 'HTML Manuals' },
  { pattern: '/projects/:id/manuals/review', label: 'Review & Changes' },
  { pattern: '/projects/:id/manuals/:manualId', label: 'Manual' },
  { pattern: '/projects/:id/ai', label: 'AI' },
  { pattern: '/projects/:id/review', label: 'Review & Changes' },
  { pattern: '/projects/:id/csdb', label: 'CSDB' },
  { pattern: '/projects/:id/csdb/editor/:dmId', label: 'DM Editor' },
  { pattern: '/projects/:id/import', label: 'Import' },
  { pattern: '/projects/:id/import/bulk/:jobId', label: 'Bulk Import' },
  { pattern: '/projects/:id/graphics', label: 'Graphics & ICN' },
  { pattern: '/projects/:id/applicability', label: 'Applicability & CIR' },
  { pattern: '/projects/:id/publishing', label: 'Publishing' },
  { pattern: '/profile', label: 'Profile' },
  // PROFILE-SLIMDOWN — `/profile/security` is now a Navigate redirect
  // to `/profile`. The breadcrumb entry is retained so the same label
  // resolves during the redirect frame for bookmarked deep links.
  { pattern: '/profile/security', label: 'Profile' },
  { pattern: '/admin', label: 'Admin' },
  { pattern: '/admin/users', label: 'Manage Users' },
  { pattern: '/admin/cage-codes', label: 'CAGE Codes' },
  { pattern: '/admin/equipment', label: 'Equipment Catalogue' },
  { pattern: '/admin/roles', label: 'Roles & Permissions' },
  { pattern: '/admin/s1000d', label: 'S1000D Configurations' },
  { pattern: '/admin/org', label: 'Organisation & System' },
  { pattern: '/admin/security', label: 'User Access & Security' },
  { pattern: '/admin/ai', label: 'AI Provider Settings' },
  { pattern: '/admin/help', label: 'Help Centre' },
  { pattern: '/search', label: 'Search' },
  { pattern: '/help', label: 'Help' },
];

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuidLike(segment: string): boolean {
  return UUID_LIKE.test(segment);
}

/**
 * Build the header breadcrumb trail for the current pathname.
 *
 * Two important behaviors that exist to prevent regressions:
 *
 *   1. The `:id` segment of `/projects/:id/...` is NEVER rendered as
 *      a raw UUID. It is replaced with the project name (looked up
 *      from `projectsById`) and the link target is forced to
 *      `/projects/:id/dashboard` — NOT `/projects/:id`, which is not
 *      a real route and would 404. If the project name is not
 *      available (still loading, or not in the active list), the
 *      label falls back to "Project".
 *
 *   2. Any other UUID-shaped segment that does NOT match a
 *      `ROUTE_LABELS` pattern is dropped from the breadcrumb entirely
 *      so we never accidentally expose a database id. Nested routes
 *      whose terminal segment is an id (manual / DM editor / bulk
 *      import job) MUST be added to `ROUTE_LABELS` so they label
 *      correctly; the UUID drop is a defensive fallback only.
 */
export function buildCrumbs(
  pathname: string,
  projectsById?: Map<string, ProjectLookupEntry>,
): Crumb[] {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Crumb[] = [];
  let path = '';
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i] ?? '';
    path += `/${seg}`;
    const match = ROUTE_LABELS.find((r) => matchPath({ path: r.pattern, end: true }, path));

    // Special case — the project-id crumb. The combination
    // `/projects/:id` matches no `ROUTE_LABELS` pattern by design (no
    // such route exists). We surface the project name + redirect any
    // click into the project's Dashboard so the breadcrumb is both
    // human-readable AND clickable without 404ing.
    if (i === 1 && segments[0] === 'projects' && segments.length > 1) {
      const project = projectsById?.get(seg) ?? null;
      const label = project?.name ?? 'Project';
      // Always link the project crumb to the project's Dashboard —
      // even when the user is already on that route. Clicking it then
      // is a no-op navigation, which is preferable to a dead link.
      const to = `/projects/${seg}/dashboard`;
      crumbs.push({ label, to });
      continue;
    }

    // Defensive — if a non-project-id segment looks like a UUID AND
    // we have no label match, drop it. This keeps unexpected nested
    // ids out of the breadcrumb.
    if (!match && isUuidLike(seg)) {
      continue;
    }

    const label = match?.label ?? seg;
    const to = i < segments.length - 1 ? path : undefined;
    crumbs.push({ label, to });
  }
  return crumbs;
}
